/**
 * Decode consecutive 16-bit Modbus registers as one value, the way GatewayPro
 * does (internal/protocols/modbus orderedBytes + decodeTag): the registers'
 * bytes are put into big-endian (ABCD) order for the device's word order,
 * then read as the point's data type.
 *
 * - big (ABCD, the Modbus default): as read, high word first.
 * - little (DCBA): every byte reversed.
 * - cdab: the 16-bit words reversed, the bytes within each word kept.
 * - badc: the words kept, the two bytes within each word swapped.
 *
 * So holding 0 = 17541 (0x4485) and holding 1 = 40960 (0xA000) read as a
 * float32 ABCD are 0x4485A000 = 1069.0. Pure, so it is unit-tested.
 */

import type { ModbusWordOrder } from "@/lib/api/things";

/**
 * The word orders as the Points step offers them, named by the byte layout the
 * device sends (big = ABCD, the Modbus default; little = DCBA, the MELSEC default).
 */
export const WORD_ORDER_CHOICES: Array<{ value: ModbusWordOrder; label: string }> = [
  { value: "big", label: "ABCD (big)" },
  { value: "cdab", label: "CDAB" },
  { value: "badc", label: "BADC" },
  { value: "little", label: "DCBA (little)" },
];

/** The numeric types a run of registers can be decoded as. */
export type RegisterNumberType =
  | "int16"
  | "uint16"
  | "int32"
  | "uint32"
  | "int64"
  | "uint64"
  | "float32"
  | "float64";

/** How many 16-bit registers a type spans: 1, 2 or 4 (bool counts as one). */
export function registersFor(type: string): number {
  switch (type) {
    case "int32":
    case "uint32":
    case "float32":
      return 2;
    case "int64":
    case "uint64":
    case "float64":
    case "double":
      return 4;
    default:
      return 1;
  }
}

/** The registers' bytes in big-endian (ABCD) order for a word order, as the gateway reorders them. */
export function orderedBytes(words: readonly number[], order: ModbusWordOrder = "big"): number[] {
  const src: number[] = [];
  for (const w of words) src.push((w >> 8) & 0xff, w & 0xff);
  if (words.length < 2) return src;
  switch (order) {
    case "little":
      return [...src].reverse();
    case "cdab": {
      const out: number[] = [];
      for (let i = words.length - 1; i >= 0; i--) out.push(src[i * 2], src[i * 2 + 1]);
      return out;
    }
    case "badc": {
      const out: number[] = [];
      for (let i = 0; i + 1 < src.length; i += 2) out.push(src[i + 1], src[i]);
      return out;
    }
    default:
      return src;
  }
}

/**
 * `words` (raw register contents, first register first) read as `type` in
 * `order`. Null when there are fewer words than the type spans. A 64-bit
 * integer is returned as the nearest double.
 */
export function decodeRegisters(
  words: readonly number[],
  type: RegisterNumberType,
  order: ModbusWordOrder = "big",
): number | null {
  const n = registersFor(type);
  if (words.length < n) return null;
  const bytes = orderedBytes(
    words.slice(0, n).map((w) => w & 0xffff),
    order,
  );
  const view = new DataView(new Uint8Array(bytes).buffer);
  switch (type) {
    case "int16":
      return view.getInt16(0);
    case "uint16":
      return view.getUint16(0);
    case "int32":
      return view.getInt32(0);
    case "uint32":
      return view.getUint32(0);
    case "float32":
      return view.getFloat32(0);
    case "int64":
      return Number(view.getBigInt64(0));
    case "uint64":
      return Number(view.getBigUint64(0));
    case "float64":
      return view.getFloat64(0);
  }
}
